import React, { useState, useEffect } from 'react';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import Icon from '../assets/icon.svg';
import { setAuthData, getApiUrl } from '../utils/tokenUtils';

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if user just registered
  useEffect(() => {
    if (searchParams.get('registered') === 'true') {
      setSuccessMessage('Registration successful! Please check your email inbox to verify your email address. Click the verification link to activate your account.');
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(getApiUrl('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Server error: Invalid response format');
      }

      const data = await response.json();

      if (!response.ok) {
        // Doğrulama yapılmadıysa otomatik doğrulama e-postası gönder ve yönlendir
        if (data.emailVerified === false) {
          try {
          await fetch(getApiUrl('/api/auth/verify-email/resend'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: formData.email })
            });
          } catch (_) {}

          // VerifyEmail sayfasının kullanıcıdan geldiğini belirtmek için işaret bırak
          localStorage.setItem('pendingVerificationEmail', formData.email);
          localStorage.setItem('pendingVerificationRegistered', 'false');
          localStorage.setItem('verifyOrigin', 'login');
          navigate('/verify-email');
          return;
        }
        throw new Error(data.message || 'Login failed');
      }

      console.log('Login response:', data);
      
      // Check if email is verified BEFORE calling onLogin
      if (data.user.emailVerified === false) {
        try {
          await fetch(getApiUrl('/api/auth/verify-email/resend'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: data.user.email })
          });
        } catch (_) {}

        // Store email in localStorage for verification page
        localStorage.setItem('pendingVerificationEmail', data.user.email);
        localStorage.setItem('pendingVerificationRegistered', 'false'); // Not from registration
        localStorage.setItem('verifyOrigin', 'login');
        
        // Navigate directly to verify email page
        navigate('/verify-email');
        return;
      }
      
      // Only proceed if email is verified
      setAuthData(data.token, data.user);
      console.log('Calling onLogin with user data:', data.user);
      onLogin(data.user);
      console.log('Navigating to dashboard...');
      navigate('/dashboard');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <Link to="/" className="inline-block">
            <img src={Icon} alt="AppActs" className="h-12 w-auto mx-auto" />
          </Link>
          <h2 className="mt-6 text-3xl font-bold text-white">
            Sign in to your account
          </h2>
          <p className="mt-2 text-sm text-gray-400">
            Welcome back! Please sign in to your account.
          </p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="bg-gray-800 rounded-lg p-6">
            {/* Messages */}
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-900/20 text-red-400 flex items-center space-x-2">
                <FaEnvelope />
                <span className="text-sm">{error}</span>
              </div>
            )}
            
            {successMessage && (
              <div className="mb-4 p-3 rounded-lg bg-green-900/20 text-green-400 flex items-center space-x-2">
                <FaCheckCircle />
                <span className="text-sm">{successMessage}</span>
              </div>
            )}

            <div className="space-y-4">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your email address"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-10 py-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full mt-6 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaLock />}
              <span>{loading ? 'Signing in...' : 'Sign in'}</span>
            </button>
          </div>

          {/* Links */}
          <div className="text-center space-y-2">
            <div>
              <Link
                to="/forgot-password"
                className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
              >
                Forgot your password?
              </Link>
            </div>
            <div>
              <Link
                to="/register"
                className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
              >
                Don't have an account? Sign up
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login; 